import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { StaffProfileStoresItem } from '@nucleus-commerce/api-client/admin';
import { formFieldPath, isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';

import { useBulkErrorMessage } from '@/shared/bulk/use-bulk-error-message';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { displayTimeZone } from '@/shared/store/store-context';

import { toScheduleRequest, type ScheduleRequest } from '../api/mappers';
import {
  bulkScheduleSchema,
  scheduleZone,
  type BulkScheduleForm,
} from '../schemas/bulk-schedule-schema';

import { StoreChecklist } from './store-checklist';

/** Fields backend errors may land on; any other path goes to `root.server`. */
const FORM_FIELDS: ReadonlySet<string> = new Set(['storeIds', 'publishAt', 'unpublishAt']);
/** A refusal of one sent store, `storeIds.2` once the `body.` prefix is gone. */
const STORE_PATH = /^storeIds\.(\d+)$/;

export interface BulkScheduleDialogProps {
  /** How many products the action covers; null keeps the dialog closed. */
  count: number | null;
  stores: ReadonlyArray<Pick<StaffProfileStoresItem, 'id' | 'name' | 'timezone'>>;
  pending: boolean;
  /** Sends the bulk `schedule` action; a rejection is shown here (field errors on their field). */
  onSubmit: (request: ScheduleRequest) => Promise<unknown>;
  onClose: () => void;
}

function Body({
  count,
  stores,
  pending,
  onSubmit,
  onClose,
}: Omit<BulkScheduleDialogProps, 'count'> & { count: number }) {
  const { t } = useTranslation(['products', 'common', 'errors']);
  const message = useBulkErrorMessage();
  const form = useForm<BulkScheduleForm>({
    resolver: useI18nResolver(bulkScheduleSchema),
    defaultValues: { storeIds: [], publishAt: '', unpublishAt: '' },
  });
  const storeIds = useWatch({ control: form.control, name: 'storeIds' });
  const zone = scheduleZone(stores, storeIds, displayTimeZone);
  const zoneOf = (storeId: string) => {
    const store = stores.find((s) => s.id === storeId);
    return store ? displayTimeZone(store.timezone) : undefined;
  };

  const storeName = (storeId: string | undefined) =>
    stores.find((store) => store.id === storeId)?.name;

  /**
   * Field errors onto their field. A refused store (`storeIds.N`, e.g. one that is unreachable)
   * shows under the checklist, named from the ids sent; paths the form lacks land on root.
   */
  const applyStoreErrors = (error: unknown, sent: readonly string[]) => {
    if (
      !applyFieldErrors(error, form.setError, {
        knownFields: FORM_FIELDS,
        mapField: (field) => (STORE_PATH.test(field) ? 'storeIds' : field),
      })
    ) {
      return false;
    }
    if (!isApiError(error)) return true;
    const refused = error.details.flatMap((detail) => {
      const match = STORE_PATH.exec(formFieldPath(detail.field));
      if (!match) return [];
      const name = storeName(sent[Number(match[1])]);
      return [
        name
          ? t('products:bulk.schedule.storeRefused', { store: name, message: detail.message })
          : detail.message,
      ];
    });
    if (refused.length > 0) {
      form.setError('storeIds', { type: 'server', message: refused.join(' ') });
    }
    return true;
  };

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(toScheduleRequest(values, zone.timeZone));
    } catch (error) {
      if (applyStoreErrors(error, values.storeIds)) return;
      const requestId = isApiError(error) ? error.requestId : undefined;
      form.setError('root.server', {
        type: 'server',
        message: requestId
          ? `${message(error)} ${t('errors:requestIdShort', { requestId })}`
          : message(error),
      });
    }
  });
  const rootError = form.formState.errors.root?.server?.message;

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{t('products:bulk.schedule.title', { count })}</DialogTitle>
          <DialogDescription>{t('products:bulk.schedule.description')}</DialogDescription>
        </DialogHeader>
        <FormField
          control={form.control}
          name="storeIds"
          render={({ field }) => (
            <FormItem>
              <StoreChecklist
                legend={t('products:bulk.storesLabel')}
                stores={stores}
                value={field.value}
                onChange={field.onChange}
                describe={zoneOf}
                idPrefix="bulk-schedule-store"
              />
              <FormMessage />
            </FormItem>
          )}
        />
        {(['publishAt', 'unpublishAt'] as const).map((name) => (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t(`products:bulk.schedule.${name}In`, { timeZone: zone.timeZone })}
                </FormLabel>
                <FormControl>
                  <DateTimePicker timeZone={zone.timeZone} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
        <div className="text-muted-foreground flex flex-col gap-1 text-xs">
          <p>{t('products:bulk.schedule.clock', { timeZone: zone.timeZone })}</p>
          {zone.otherZones.length > 0 ? (
            <p role="note">
              {t('products:bulk.schedule.otherZones', {
                stores: zone.otherZones
                  .map((store) => `${store.name} (${displayTimeZone(store.timezone)})`)
                  .join(', '),
                timeZone: zone.timeZone,
              })}
            </p>
          ) : null}
          <p>{t('products:bulk.schedule.hint')}</p>
        </div>
        {rootError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{rootError}</AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? t('products:bulk.working') : t('products:bulk.schedule.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/**
 * "Schedule publishing…" for the selection (D-181, one-shot): the stores, a publish and/or an
 * unpublish time on one store's clock, and a note when the chosen stores keep other clocks. Each
 * product gets the dates in each store, added there as a draft when missing; a product a rotation
 * owns in a store comes back per id as SCHEDULE_CONFLICT in the result dialog.
 */
export function BulkScheduleDialog(props: BulkScheduleDialogProps) {
  const { count, onClose } = props;
  return (
    <Dialog open={count !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-md">
        {count !== null ? <Body {...props} count={count} /> : null}
      </DialogContent>
    </Dialog>
  );
}
